import { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import { postEvent } from "../../../../../scripts/event";
import Button from "../../../../UiComponents/Button";
import Modal from "../../../../UiComponents/Modal";
import ToggleButton from "../../../../UiComponents/ToggleButton";
import { FieldButton } from "../../../fields/styling/FieldStyles";
import { ButtonRowContainer, ModalContainer, ModalHeader } from "../../../../panel/ModalStyles";
import RemoteConnection from "../actions/RemoteConnection";
import { isLocalApp } from "../../../../../scripts/server/server";
import { hasRole, roles } from "../../../../permission/permissions";
import UserContext from "../../../../../hooks/UserContext";
import { updateHub } from "../../../../../scripts/hub";
import theme from "../../../../../UI/theme";

const HubSettingRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 12px;
    margin-bottom: 16px;
    border: 1px solid ${theme.colors.grayBorder};
    background: ${theme.colors.darkSpace80};
`;

const HubSettingTextContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const HubSettingTitle = styled.div`
    font-size: 0.95rem;
    font-weight: 600;
    color: ${theme.colors.white};
`;

const HubSettingDescription = styled.div`
    font-size: 0.85rem;
    color: ${theme.colors.textGray};
`;

const HubSettingControlContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const HubSettingState = styled.div`
    min-width: 58px;
    font-size: 0.8rem;
    font-weight: 600;
    color: ${props => props.enabled ? theme.colors.greenEnergy : theme.colors.textGray};
    text-align: right;
`;


const SystemActionsField = ({ hub }) => {
    const [showModal, setShowModal] = useState(false);
    const [ignoreHubErrors, setIgnoreHubErrorsState] = useState(!!hub?.ignoreHubErrors);
    const [savingIgnoreHubErrors, setSavingIgnoreHubErrors] = useState(false);
    const userContext = useContext(UserContext);

    useEffect(() => {
        setIgnoreHubErrorsState(!!hub?.ignoreHubErrors);
    }, [hub?.ignoreHubErrors]);

    const performAction = async (command) => {
        try {
            await postEvent(hub.hubId, hub.customerId, command);
            setShowModal(false);
        } catch (err) {
            console.log('Failed to perform action');
        }
    }

    const setIgnoreHubErrors = async (newState) => {
        if (savingIgnoreHubErrors) {
            return;
        }

        const previousState = ignoreHubErrors;
        const nextState = !!newState;

        setIgnoreHubErrorsState(nextState);
        setSavingIgnoreHubErrors(true);

        const updatedHub = {
            hubId: hub.hubId,
            ignoreHubErrors: nextState
        }

        try {
            await updateHub(updatedHub);
        } catch (err) {
            setIgnoreHubErrorsState(previousState);
            console.log('Failed to update ignore hub errors state');
        } finally {
            setSavingIgnoreHubErrors(false);
        }
    }

    return <>
        <FieldButton small tertiary onClick={() => setShowModal(true)}>System Actions</FieldButton>
        <Modal show={showModal} close={() => setShowModal(false)}>
            <ModalContainer>
                <ModalHeader>System Actions</ModalHeader>
                <ButtonRowContainer>
                    <Button secondary onClick={() => performAction('RESTART_APP')}>Restart program</Button>
                    <Button secondary onClick={() => performAction('REBOOT')}>Reboot</Button>
                    <Button secondary onClick={() => performAction('REBOOT_HAT')}>Reboot hat</Button>
                </ButtonRowContainer>

                <h4>Admin Hub Settings</h4>
                <HubSettingRow>
                    <HubSettingTextContainer>
                        <HubSettingTitle>Ignore hub errors</HubSettingTitle>
                        <HubSettingDescription>
                            Suppresses hub errors in the app.
                        </HubSettingDescription>
                    </HubSettingTextContainer>
                    <HubSettingControlContainer>
                        <ToggleButton checked={ignoreHubErrors} onToggle={setIgnoreHubErrors} />
                        <HubSettingState enabled={ignoreHubErrors}>
                            {ignoreHubErrors ? 'Enabled' : 'Disabled'}
                        </HubSettingState>
                    </HubSettingControlContainer>
                </HubSettingRow>


                {!isLocalApp() && hasRole(userContext, roles.admin) ? <>
                    <div>
                        <h4>Remote ssh connection</h4>
                        <RemoteConnection hub={hub} />
                    </div>
                </> : <></>}
            </ModalContainer>
        </Modal>
    </>
}

export default SystemActionsField;

